import {
    Box,
    Flex,
    IconButton,
    Icons,
    ThemeBorderVariable,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeFontVariable,
    ThemeRadiusVariable,
} from "@caspeco/casper-ui-library";
import React, { useRef, useState } from "react";

interface QuantityInputProps {
    quantity: number;
    onChange: (quantity: number) => void;
    inputAriaLabel: string;
    decreaseAriaLabel: string;
    increaseAriaLabel: string;
    min?: number;
    max?: number;
    size?: "sm" | "lg";
    isDisabled?: boolean;
}

/**
 * Stepper with a typeable number field: the +/- buttons stay for small adjustments,
 * while larger parties can select the field and type the number directly.
 */
function QuantityInput({
    quantity,
    onChange,
    inputAriaLabel,
    decreaseAriaLabel,
    increaseAriaLabel,
    min = 0,
    max = 99,
    size = "sm",
    isDisabled = false,
}: QuantityInputProps) {
    // Holds the digits typed so far; null means the committed quantity is shown.
    const [draft, setDraft] = useState<string | null>(null);
    // The field keeps its value on focus, so the first digit typed has to replace that
    // value rather than extend it — otherwise typing 23 over a 2 would read 223.
    const isFirstKeystrokeRef = useRef(false);

    // Callers derive their max by subtraction (a shared budget split across two fields),
    // so it can land below min. Clamping up to min keeps the bounds from inverting, which
    // would otherwise let the clamps below settle on a max lower than the value allows.
    const upperBound = Math.max(min, max);

    const isLarge = size === "lg";
    // Wider than a plain stepper so the field between the buttons is a comfortable tap
    // target; it stretches to the full row height for the same reason.
    const width = isLarge ? "180px" : "140px";

    const commit = (value: number) => {
        if (value !== quantity) {
            onChange(value);
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const raw = event.target.value;
        const shown = draft ?? String(quantity);
        const wasFirstKeystroke = isFirstKeystrokeRef.current;
        isFirstKeystrokeRef.current = false;

        // Keep only what was appended to the untouched value, so the first digit stands
        // alone. A deletion or a caret moved off the end falls through to the raw value.
        const typed =
            wasFirstKeystroke && raw.length > shown.length && raw.startsWith(shown)
                ? raw.slice(shown.length)
                : raw;

        const digits = typed.replace(/\D/g, "");
        if (!digits) {
            setDraft("");
            return;
        }

        const capped = Math.min(Number(digits), upperBound);
        setDraft(String(capped));
        // A value below the minimum stays in the draft only; blur raises it to min so
        // that typing "15" when the minimum is 2 isn't cut short after the first digit.
        if (capped >= min) {
            commit(capped);
        }
    };

    const handleBlur = () => {
        isFirstKeystrokeRef.current = false;
        // Emptied and below-minimum settle the same way. Focus alone never writes a draft,
        // so leaving an untouched field commits nothing.
        if (draft !== null && (draft === "" || Number(draft) < min)) {
            commit(min);
        }
        setDraft(null);
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        isFirstKeystrokeRef.current = true;
        // Put the caret after the number. Deferred because a click's own caret placement
        // happens after this handler runs and would otherwise win.
        const input = event.currentTarget;
        requestAnimationFrame(() => {
            const end = input.value.length;
            input.setSelectionRange(end, end);
        });
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            event.currentTarget.blur();
        }
    };

    const step = (delta: number) => {
        setDraft(null);
        commit(Math.min(Math.max(quantity + delta, min), upperBound));
    };

    return (
        <Flex w={width}>
            <Box
                w="full"
                border={ThemeBorderVariable.XSmall}
                borderColor={ThemeColorVariable.OnBackgroundBorder}
                borderRadius={ThemeRadiusVariable.XLarge}
                bg={ThemeColorVariable.Surface}
                opacity={0.88}
                // Same focus ring the library gives its inputs, moved to the frame so the
                // whole control lights up rather than the bare number inside it.
                // @ts-expect-error // ! TODO: Include _focusWithin prop in ThemeStyleProps for box in casper-ui-library: Props are spread to Box which supports _focusWithin, but TypeScript does not recognize it due to missing type definitions in casper-ui-library
                _focusWithin={{
                    borderColor: ThemeColorVariable.Info,
                    boxShadow: `0 0 0 4px ${ThemeColorVariable.InfoContainer}`,
                }}
            >
                <Flex align="center" justifyContent="space-between">
                    <IconButton
                        variant="ghost"
                        icon={Icons.Decrease}
                        aria-label={decreaseAriaLabel}
                        size={isLarge ? "lg" : "md"}
                        isDisabled={isDisabled || quantity <= min}
                        onClick={() => step(-1)}
                    />
                    <Box
                        as="input"
                        type="text"
                        inputMode="numeric"
                        aria-label={inputAriaLabel}
                        value={draft ?? String(quantity)}
                        disabled={isDisabled}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onKeyDown={handleKeyDown}
                        flex={1}
                        minW={0}
                        alignSelf="stretch"
                        textAlign="center"
                        bg="transparent"
                        border="none"
                        cursor="text"
                        fontFamily={ThemeFontVariable.SubHeader}
                        fontSize={
                            isLarge ? ThemeFontSizeVariable.XLarge : ThemeFontSizeVariable.Large
                        }
                        color={ThemeColorVariable.OnSurface}
                        style={{ outline: "none" }}
                    />
                    <IconButton
                        variant="ghost"
                        icon={Icons.Increase}
                        aria-label={increaseAriaLabel}
                        size={isLarge ? "lg" : "md"}
                        isDisabled={isDisabled || quantity >= upperBound}
                        onClick={() => step(1)}
                    />
                </Flex>
            </Box>
        </Flex>
    );
}

export { QuantityInput };
